import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import deDE from '@angular/common/locales/de';
import enGB from '@angular/common/locales/en-GB';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { Store } from '@ngrx/store';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { DatePickerModule } from '@rtr-factory/ui/date-picker';
import {
  DESKTOP_MODE_APPLICATION_WIDTH_BREAKPOINT,
  DESKTOP_MODE_COMPONENT_WIDTH_BREAKPOINT,
  INDEXED_DB_DATABASE_NAME,
} from '@rtr-factory/utils/injection-tokens';
import { LocalStorageService, ScreenObserver } from '@rtr-factory/utils/services';
import {
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  createInterceptorCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { LocalStorageStoreModule } from 'local-storage';
import { CategoriesStoreModule } from 'rio-categories-data-access';
import {
  API_PREFIX,
  AppConfigJsonModel,
  ConfigService,
  OrganizationsService,
  OrganizationsStoreModule,
  RouterStoreModule,
  SystemStoreModule,
  ToastStoreModule,
  UserService,
  UserStoreModule,
  configProvider,
} from 'rio-core';
import { PromptChainsStoreModule } from 'rio-prompt-chains-data-access';
import { PromptsStoreModule } from 'rio-prompts-data-access';
import { RioTrackingStoreModule, TrackingService } from 'rio-tracking';
import { IconRegistryModule } from 'rio-ui';
import { UploadStoreModule } from 'rio-upload-data-access';
import { RioStoreModule } from '../initializers/rio-store.module';
import { rioUserInitializer } from '../initializers/rio-user-initializer';
import { SentryErrorHandlerService, initializeSentry } from '../initializers/sentry-initializer';
import { APIErrorHandlerInterceptor } from '../interceptors/api-error-handler.interceptor';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';

export const initializeApp = async () => {
  const config: AppConfigJsonModel = await fetch('assets/config.json').then(res => res?.json());

  const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: /^(?!.*\/assets\/).*/,
    bearerPrefix: 'Bearer',
  });

  const appConfig: ApplicationConfig = {
    providers: [
      provideZoneChangeDetection(),
      provideRouter(appRoutes, withComponentInputBinding()),
      importProvidersFrom(RioStoreModule),
      importProvidersFrom(UserStoreModule),
      importProvidersFrom(SystemStoreModule),
      importProvidersFrom(OrganizationsStoreModule),
      importProvidersFrom(BrowserAnimationsModule),
      importProvidersFrom(LocalStorageStoreModule),
      importProvidersFrom(ToastStoreModule),
      importProvidersFrom(CategoriesStoreModule),
      importProvidersFrom(PromptsStoreModule),
      importProvidersFrom(PromptChainsStoreModule),
      importProvidersFrom(RouterStoreModule),
      importProvidersFrom(RioTrackingStoreModule),
      importProvidersFrom(UploadStoreModule),
      importProvidersFrom(DatePickerModule),
      importProvidersFrom(IconRegistryModule),
      provideHttpClient(),
      provideAppInitializer(() => {
        const initializerFn = initializeSentry();
        return initializerFn();
      }),
      {
        provide: ErrorHandler,
        useClass: SentryErrorHandlerService,
      },
      provideAppInitializer(() => {
        const initializerFn = configProvider(inject(ConfigService));
        return initializerFn();
      }),
      provideKeycloak({
        config: {
          url: config['auth-server-url'],
          clientId: config.resource,
          realm: config.realm,
        },
      }),
      provideAppInitializer(() => {
        const initializerFn = rioUserInitializer(
          inject(Keycloak),
          inject(Store),
          inject(OrganizationsService),
          inject(UserService),
          inject(TrackingService),
          inject(ConfigService),
          inject(LocalStorageService),
        );
        return initializerFn();
      }),
      provideAppInitializer(() => registerLanguages()),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: APIErrorHandlerInterceptor,
        multi: true,
      },
      {
        provide: API_PREFIX,
        useValue: '/api/v1',
      },
      {
        provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
        useValue: [urlCondition],
      },
      provideHttpClient(withInterceptorsFromDi(), withInterceptors([includeBearerTokenInterceptor])),
      provideTranslateService({
        loader: provideTranslateHttpLoader({
          prefix: '/assets/i18n/',
          suffix: '.json',
        }),
      }),
      { provide: DESKTOP_MODE_COMPONENT_WIDTH_BREAKPOINT, useValue: 616 },
      { provide: DESKTOP_MODE_APPLICATION_WIDTH_BREAKPOINT, useValue: 1024 },
      { provide: INDEXED_DB_DATABASE_NAME, useValue: 'rio-rewrite-sessions' },
      ScreenObserver,
    ],
  };

  function registerLanguages() {
    registerLocaleData(deDE, 'de-DE');
    registerLocaleData(enGB, 'en-GB');
  }

  await bootstrapApplication(AppComponent, appConfig);
};
